import { Factory } from './factory';
import { QuickLink, QuickLinkType } from '../quick-link';
import { Position } from '../position';
import { QuickLinkUtils } from '../../utils/quick-link-utils';
import { Injectable } from '@angular/core';
import { ApplicationStateService } from '../../services/application-state.service';
import { Page } from '../page';

@Injectable({
    providedIn: 'root'
})
export class QuickLinkFactory implements Factory<QuickLink> {

    constructor(
        private applicationStateService: ApplicationStateService
    ) {
    }

    buildFromFactory(): QuickLink {
        return undefined;
    }

    buildFromObject(jsonObject: QuickLinkInterface): QuickLink {
        const quickLink = new QuickLink();
        quickLink.id = jsonObject.id;
        quickLink.pageId = jsonObject.pageId;

        const position = new Position();
        position.x = +jsonObject.position.x;
        position.y = +jsonObject.position.y;
        position.w = +jsonObject.position.w;
        position.h = +jsonObject.position.h;

        quickLink.position = position;
        quickLink.link = jsonObject.link;

        if (jsonObject.meta && jsonObject.meta.menu) {
            quickLink.menu = jsonObject.meta.menu;
        }
        const prefix = this.applicationStateService.bookConfig.quickLinkPrefix;

        quickLink.type = QuickLinkUtils.getMatchingQuickLinkType(quickLink.link, prefix);
        quickLink.isSignable = QuickLinkUtils.isSignable(quickLink, prefix);
        if (quickLink.isOther() && this.applicationStateService.bookConfig.bookSource === 'sia') {
            quickLink.type = QuickLinkType.SIA;
            quickLink.isSignable = true;
        }

        if (jsonObject.sub_type === 7) { // Detected digi fit menu link
            quickLink.type = QuickLinkType.DIGI_FIT;
        }

        if (jsonObject.sub_type === 8) { // Manual Yedo links
            quickLink.type = QuickLinkType.YEDO;
            quickLink.isSignable = true;
        }
        if (jsonObject.redirect && quickLink.isOther()) { // Special case for yedo redirects.
            quickLink.type = QuickLinkUtils.getMatchingQuickLinkType(jsonObject.redirect, prefix);
            quickLink.isSignable = quickLink.isYedo();
        }

        quickLink.showSolution = jsonObject.showSolution;
        quickLink.redirect = jsonObject.redirect;
        quickLink.slug = jsonObject.slug;
        quickLink.preview = jsonObject.preview;

        return quickLink;
    }

    createSolutionQuickLink(quickLink: QuickLink, pageWidth: number, pageHeight: number, context?: Context): QuickLink {
        const solutionQuicklink = new QuickLink();
        solutionQuicklink.id = quickLink.id * -1;
        solutionQuicklink.pageId = quickLink.pageId;
        solutionQuicklink.position = quickLink.position;
        solutionQuicklink.link = quickLink.link;
        solutionQuicklink.slug = quickLink.slug;
        solutionQuicklink.isSolutionLink = true;
        solutionQuicklink.bypassCheck = quickLink.bypassCheck;
        solutionQuicklink.type = quickLink.type;
        solutionQuicklink.combinedLink = quickLink.combinedLink;
        solutionQuicklink.found = quickLink.found;
        solutionQuicklink.showSolution = quickLink.showSolution;
        solutionQuicklink.redirect = quickLink.redirect;
        solutionQuicklink.wasClicked = quickLink.wasClicked;
        solutionQuicklink.external = quickLink.external;

        return solutionQuicklink;
    }

    getDropdownPossition(quickLink: QuickLink, pageWidth: number, pageHeight: number, context?: Context): Position {
        const baseWidth = 2.5;
        const dropdownMargin = 1;
        const oldPosition = JSON.parse(JSON.stringify(quickLink.position));
        const offsetPosition = new Position();
        switch (this.applicationStateService.bookConfig.dropdownPosition) {
            case 'left':
                offsetPosition.x = -(baseWidth + dropdownMargin);
                break;
            case 'right':
                offsetPosition.x = oldPosition.w + dropdownMargin;
                break;
            case 'auto':
            default:
                if (oldPosition.x <= 50) {
                    offsetPosition.x = -(baseWidth + dropdownMargin);
                } else {
                    offsetPosition.x = oldPosition.w + dropdownMargin;
                }
        }

        // Create fake quicklink to get the possition
        const dropdownQuicklink = new QuickLink();
        dropdownQuicklink.link = quickLink.link;
        dropdownQuicklink.position = oldPosition;
        dropdownQuicklink.position.x += offsetPosition.x;
        dropdownQuicklink.position.y -= 0.2;
        dropdownQuicklink.position = Position.toPosition(dropdownQuicklink.position);

        if (!this.applicationStateService.bookConfig.displayDropdown) {
            dropdownQuicklink.zIndex = 3;
        }

        this.updatePosition(dropdownQuicklink, context);

        // We want the buttons to be ca. 4.5% of the page width. The height of the button must
        // be multiplied with the relation of width/height to be a square.
        dropdownQuicklink.position.w = baseWidth;
        dropdownQuicklink.position.h = (pageWidth / pageHeight) * baseWidth;

        return dropdownQuicklink.position;
    }

    private updatePosition(solutionQuicklink: QuickLink, context?: Context): void {

        const domainName = this.applicationStateService.platformDomain;
        const gm3BookIds = [1408, 2261];
        if (gm3BookIds.includes(context.bookId)) {

            if (context.page.index === 95) {
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm3b286`, -0.8);
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm3b287`, 1.5);
            }
        }

        const gm4BookIds = [1409, 2262];
        if (gm4BookIds.includes(context.bookId)) {

            if (context.page.index === 25) {
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm4b23`, -2.2);
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm4b24`, 0.1);
            }

            if (context.page.index === 29) {
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm4b59`, -0.4);
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm4b60`, 1.2);
            }

            if (context.page.index === 31) {
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm4b77`, -2.2);
            }

            if (context.page.index === 66) {
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm4b194`, -1.5);
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm4b195`, 1.5);
            }

            if (context.page.index === 67) {
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm4b202`, -2);
            }

            if (context.page.index === 189) {
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm4b687`, -1.5);
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm4b688`, 1.5);
            }

            if (context.page.index === 212) {
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm4b750`, -0.5);
                this.updateQuickLinkYPosition(solutionQuicklink, `${domainName}/gm4b751`, 1.5);
            }
        }

    }

    private updateQuickLinkYPosition(solutionQuicklink: QuickLink, link: string, value: number): void {
        if (solutionQuicklink.link === link) {
            solutionQuicklink.position.moveY(value);
        }
    }
}

export class Context {
    page: Page;
    bookId: number;
}

export interface QuicklinkMetaData {
    menu: {
        [key: string]: string;
    };
}

export interface QuickLinkInterface {
    id: number;
    link: string;
    pageId: number;
    position: { x: number; y: number; w: number; h: number };
    redirect: string | undefined;
    slug: string | undefined;
    type: number | undefined;
    preview: boolean | undefined;
    menu: object | undefined;
    sub_type: number | undefined;
    showSolution: boolean | undefined;
    meta: QuicklinkMetaData | undefined;
}
